<script setup>
import { getI18n, getTranslatedField } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useRegistryStore } from '@/stores/registry.store'
import { useColumns } from '@/composables/registry/discount-category-grid'

import { useEntityLock } from '@/services/entity-lock.service'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

const gridId = 'discount-category-grid'

const i18n = getI18n()
const registryStore = useRegistryStore()

const { gridRef } = dataGrid.useGridRef()
const { route, router } = dataGrid.useGridRouter()

const entityLock = useEntityLock({
  baseLockUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.discountCategoriesUrl,
  modelName: 'DiscountCategory',
  gridRef,
})

const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    delete: (e) => !e.isReadonly && !entityLock.isLocked(false, e.entityId)
  }
})

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.discountCategoriesUrl)

const columns = useColumns()

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()
const dialogTitle = (item) => defaultDialogTitle(
  item ? { ...item, name: getTranslatedField(item, 'name') } : item,
  i18n.t('common.discountCategory')
)

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 2,
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.mainInfo'),
      colCount: 4,
      items: [
        { dataField: 'supplier_brand_ref', colSpan: 2 },
        { dataField: 'code', colSpan: 2, editorOptions: { maxLength: 50 } },
        { dataField: 'name', colSpan: 3 },
        { dataField: 'is_locked', editorType: 'dxCheckBox' },
      ],
    },
    {
      itemType: 'group',
      caption: i18n.t('common.accounting'),
      colCount: 2,
      items: [
        { dataField: 'intrastat_code' },
        { itemType: 'empty' },
        { dataField: 'financial_center_cost_ref' },
        { dataField: 'trade_account_purchase_ref' },
        { dataField: 'financial_center_income_ref' },
        { dataField: 'trade_account_sale_ref' },
      ],
    },
  ]
}, {
  allowDeleting: (e) => !e.row.data.is_portal,
  onInitializedCustom: (e) => entityLock.readOnlyToolbarItems(e),
  entityLock: entityLock
}
)

const onSaved = async(e) => {
  await registryStore.fetchDiscountCategoriesAsync(true)
}

const onSaving = async(e) => {
  const change = e.changes?.[0]
  if (change?.type !== 'update' || change.key < 0) return

  const current = e.component.getVisibleRows().find(row => row.key === change.key)?.data
  if (!current?.is_portal) return

  e.cancel = true
  const data = {
    ...current,
    ...change.data,
    name: change.data.name ?? getTranslatedField(current, 'name'),
    portal_reference: current.id,
  }
  for (const field of ['id', 'translations', 'is_portal', 'supplier_brand_ref_code', 'reference_name']) delete data[field]

  await registryStore.api.createDiscountCategoryAsync(data)
  await registryStore.fetchDiscountCategoriesAsync(true)
  e.component.cancelEditData()
  await e.component.refresh()
}

const onRowRemoved = () => {
  const gridInstance = gridRef.value?.instance
  gridInstance?.refresh()
}

const onClearFilter = () => { router.replace() }

const onEditorPreparing = (e) => {
  if (e.parentType === 'dataRow' && ['supplier_brand_ref', 'code'].includes(e.dataField)) {
    e.editorOptions.readOnly = !!e.row?.data?.id
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  onEditorPreparing: onEditorPreparing,
  onRowRemoved: onRowRemoved,
  onSaving: onSaving,
  onSaved: onSaved,
  onClearFilter: onClearFilter,
  onToolbarPreparing: (e) => dataGrid.useDefaultToolbar(e, {
    addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.discountCategory')}` },
  }),
  routerFilter: {
    fields: [{brand: 'supplier_brand_ref'}],
    getRoute: () => route,
    router: router
  }
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'DiscountCategory'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :grid="gridRef"
          :entity-lock="entityLock"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
